import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Package, Calendar, Truck, ShoppingCart, ArrowRight, CheckCircle, Phone, MessageCircle, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const Services = () => {
  const services = [
    {
      icon: Clock,
      title: 'Same-Day Courier',
      description:
        'When every minute matters, our same-day courier service delivers urgency without compromise. Perfect for time-critical documents, samples, and urgent packages.',
      benefits: [
        'Collection within 60 minutes',
        'Delivery across major UK cities',
        'Real-time tracking and updates',
        'Dedicated driver for your delivery',
      ],
      useCases: ['Legal documents', 'Medical samples', 'Emergency spare parts', 'Last-minute gifts'],
      image: '/assets/sameday.jpg',
    },
    {
      icon: Package,
      title: 'Next-Day / Overnight',
      description:
        'Reliable next-day delivery across the entire UK. Book before 5pm for guaranteed delivery by 7AM the following day.',
      benefits: [
        'Nationwide coverage',
        'Guaranteed delivery times',
        'Competitive pricing',
        'Signature on delivery',
      ],
      useCases: ['Business supplies', 'Online retail orders', 'Contract deliveries', 'B2B shipments'],
      image: 'https://images.pexels.com/photos/4391470/pexels-photo-4391470.jpeg',
    },
    {
      icon: Calendar,
      title: 'Scheduled Routes & Contracts',
      description:
        'Tailored logistics solutions for businesses with regular delivery needs. We create custom routes and schedules that fit your operations perfectly.',
      benefits: [
        'Flexible scheduling',
        'Dedicated account manager',
        'Volume discounts',
        'Predictable costs',
      ],
      useCases: ['Regular supplier runs', 'Multi-site businesses', 'Wholesale distribution', 'Contract manufacturing'],
      image: '/assets/delivery-service.jpg',
    },
    {
      icon: MapPin,
      title: 'Multi-Drop Services',
      description:
        'Efficient delivery to multiple locations in one trip. Ideal for businesses needing cost-effective distribution across various destinations without compromising on speed or reliability.',
      benefits: [
        'Cost-effective multi-location delivery',
        'Optimised route planning',
        'Single collection, multiple deliveries',
        'Real-time tracking for all drops',
      ],
      useCases: ['Retail chain deliveries', 'Restaurant supply runs', 'Pharmacy distribution', 'Office supplies delivery'],
      image: '/assets/vans-2.jpeg',
    },
    {
      icon: Truck,
      title: 'Pallet & Freight Services',
      description:
        'Heavy-duty logistics for large shipments. Our freight service handles everything from single pallets to full truckloads with professional care.',
      benefits: [
        'Up to 26,000kg capacity',
        'Forklift and loading assistance',
        'Secure warehousing options',
        'Proof of delivery documentation',
      ],
      useCases: ['Manufacturing materials', 'Construction supplies', 'Bulk inventory', 'Trade show equipment'],
      image: '/assets/fork.jpeg',
    },
    {
      icon: ShoppingCart,
      title: 'E-commerce & Marketplace Deliveries',
      description:
        'Specialised fulfilment services for online retailers and marketplace sellers. Seamlessly integrate with your platform for automatic order processing.',
      benefits: [
        'API integration available',
        'Returns management',
        'Multi-channel support',
        'White-label options',
      ],
      useCases: ['Amazon FBA', 'eBay sellers', 'Shopify stores', 'WooCommerce sites'],
      image: '/assets/ecom.webp',
    },
    {
      icon: Package,
      title: 'Storage Solutions',
      description:
        'Secure, flexible warehouse storage for all your inventory needs. From short-term overflow to long-term warehousing, we provide safe, accessible storage facilities.',
      benefits: [
        '24/7 CCTV surveillance',
        'Climate-controlled options',
        'Flexible storage periods',
        'Easy access and retrieval',
      ],
      useCases: ['Inventory overflow', 'Seasonal storage', 'Product fulfillment', 'Archive storage'],
      image: '/assets/fork-lift.webp',
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Courier Services - Same-Day, Next-Day & Storage | Idris Express"
        description="Comprehensive delivery solutions: same-day courier, next-day delivery, multi-drop services, freight, e-commerce fulfilment, and secure storage. Nationwide UK coverage."
        keywords="same day courier, next day delivery, freight services, storage solutions, multi-drop delivery, e-commerce delivery, pallet delivery"
        canonicalUrl="https://idrisexpressltd.co.uk/services"
      />
      {/* Hero Section */}
      <section className="bg-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Services</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Comprehensive delivery and logistics solutions designed to meet every business need
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <service.icon className="h-8 w-8 text-gray-800" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900">{service.title}</h2>
                  </div>
                  <p className="text-gray-600 text-lg mb-6 leading-relaxed">{service.description}</p>

                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Key Benefits:</h3>
                    <ul className="space-y-2">
                      {service.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-center space-x-2">
                          <CheckCircle className="h-5 w-5 text-gray-800 flex-shrink-0" />
                          <span className="text-gray-600">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Ideal For:</h3>
                    <div className="flex flex-wrap gap-2">
                      {service.useCases.map((useCase, idx) => (
                        <span
                          key={idx}
                          className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium"
                        >
                          {useCase}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <a href="tel:07481435244" aria-label={`Call to discuss ${service.title}`}>
                      <Button className="bg-gray-800 hover:bg-gray-700 text-white">
                        <Phone className="mr-2 h-4 w-4" />
                        Call: 07481 435244
                      </Button>
                    </a>
                    <a href="https://wa.me/447481435244" target="_blank" rel="noopener noreferrer" aria-label={`Chat on WhatsApp about ${service.title}`}>
                      <Button variant="outline" className="border-gray-800 text-gray-800 hover:bg-gray-100">
                        <MessageCircle className="mr-2 h-4 w-4" />
                        WhatsApp Us
                      </Button>
                    </a>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative h-96 lg:h-[500px] rounded-lg overflow-hidden shadow-xl">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Need a Custom Solution?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Every business is unique. Let's discuss how we can tailor our services to meet your specific needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:07481435244" aria-label="Call Idris Express Ltd for a custom solution">
              <Button size="lg" className="bg-gray-800 hover:bg-gray-700 text-white text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Call: 07481 435244
              </Button>
            </a>
            <a href="https://wa.me/447481435244" target="_blank" rel="noopener noreferrer" aria-label="Contact Idris Express on WhatsApp for a custom solution">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-gray-800 text-gray-800 hover:bg-gray-100 text-lg px-8 py-6"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;